/**
 * Real-User-Monitoring beacon (perf plan WS6.2). Loads `web-vitals` on idle
 * (dynamic import — never adds bytes to the entry chunk), collects
 * onLCP/onINP/onCLS/onTTFB plus a lightweight custom route-change timing,
 * and batches everything into ONE `navigator.sendBeacon` call per page
 * hide/unload so this never competes with real traffic for bandwidth.
 *
 * Dimensions carried on every beacon: the route (normalized — dynamic ids
 * collapsed so the backend log line groups by page shape, not by entity),
 * the active workspace id (if known), and whether the persisted query cache
 * (perf plan §4.6) was warm or cold for this load — the whole point of the
 * persist-client work is to make repeat loads feel instant, so RUM needs to
 * be able to tell warm and cold loads apart in aggregate.
 */
import Cookies from 'js-cookie';
import { scheduleIdle } from './idle';
import type { pageReadySample } from './pageReadyTiming';
import { API_URL } from '@knitknot/shared/lib/appUrls';

// `/api` under the dev-server proxy; otherwise the env-resolved API origin
// (appUrls) — never a port literal, which is wrong in every worktree.
const BACKEND_URL = import.meta.env.VITE_API_BASE_URL || API_URL;
const RUM_ENDPOINT = `${BACKEND_URL}/api/v1/dev/rum`;

interface RumMetric {
  name: string;
  value: number;
  rating?: string;
  id?: string;
  route?: string;
  workspace_id?: string | null;
  cache?: string;
  build_id?: string;
  navigation?: string;
  outcome?: string;
}

export type IaMetricName =
  | 'ia-navigation'
  | 'ia-overview-first-click'
  | 'ia-legacy-redirect'
  | 'ia-workspace-state';

/**
 * Queue one content-free IA event in the existing RUM beacon. Callers pass
 * only controlled route/surface keys; question text, answer text, search
 * strings, entity names, and secrets never belong in this channel.
 */
export function trackIaMetric(name: IaMetricName, id: string): void {
  const safeId = id.toLowerCase().replace(/[^a-z0-9:._/-]+/g, '-').slice(0, 128);
  queuedMetrics.push({ name, value: 1, id: safeId });
}

/** Explicit visit metadata survives navigation before the batched beacon flush. */
export function trackDataReadyMetric(sample: NonNullable<ReturnType<typeof pageReadySample>>): void {
  installFlushListeners();
  queuedMetrics.push({
    ...sample,
    cache: cacheWarm === null ? 'unknown' : cacheWarm ? 'warm' : 'cold',
    build_id: __BUILD_ID__,
  });
}

let cacheWarm: boolean | null = null;
let queuedMetrics: RumMetric[] = [];
let flushListenersInstalled = false;

/** Set once, right after `PersistQueryClientProvider`'s restore settles
 *  (App.tsx) — whether anything was actually restored from IndexedDB, i.e.
 *  a real "warm" repeat load vs. a first-ever/expired/busted cold one. */
export function markCacheWarm(warm: boolean): void {
  cacheWarm = warm;
}

/** Collapse path segments that are entity ids (UUIDs, numeric ids, long
 *  opaque hashes/slugs) down to `:id` so the RUM log groups by page SHAPE
 *  (e.g. `/prompts/features/:id`) instead of fragmenting one metric per
 *  entity. Kept intentionally simple — this is a monitoring dimension, not a
 *  router. */
function normalizeRoute(pathname: string): string {
  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return (
    pathname
      .split('/')
      .map((segment) => {
        if (!segment) return segment;
        if (UUID_RE.test(segment)) return ':id';
        if (/^\d+$/.test(segment)) return ':id';
        if (segment.length > 24 && !/\s/.test(segment)) return ':id';
        return segment;
      })
      .join('/') || '/'
  );
}

function currentWorkspaceId(): string | null {
  return Cookies.get('workspace_id') || null;
}

function flush(): void {
  if (queuedMetrics.length === 0) return;
  const metrics = queuedMetrics;
  queuedMetrics = [];
  // The receiver accepts at most 50 metrics per beacon.
  for (let offset = 0; offset < metrics.length; offset += 50) {
    const payload = {
      route: normalizeRoute(window.location.pathname),
      workspace_id: currentWorkspaceId(),
      cache: cacheWarm === null ? 'unknown' : cacheWarm ? 'warm' : 'cold',
      metrics: metrics.slice(offset, offset + 50),
    };

    const body = JSON.stringify(payload);
    // `sendBeacon` with a plain string body defaults to `text/plain` — matches
    // the existing `dev/browser-logs` transport (services/logger.ts) and
    // avoids a CORS preflight sendBeacon can't perform anyway. The backend
    // parses the raw body as JSON regardless of Content-Type.
    const sent = typeof navigator.sendBeacon === 'function' && navigator.sendBeacon(RUM_ENDPOINT, body);
    if (!sent) {
      // Fallback for browsers/contexts without sendBeacon (or a body too large
      // for it to accept) — fire-and-forget, `keepalive` survives page unload.
      fetch(RUM_ENDPOINT, { method: 'POST', body, keepalive: true }).catch(() => {
        // Best-effort telemetry — never surface a failure to the user.
      });
    }
  }
}

function installFlushListeners(): void {
  if (flushListenersInstalled) return;
  flushListenersInstalled = true;
  // Flush on hide (tab switch / backgrounding) — the standard web-vitals
  // guidance, since `visibilitychange` fires reliably on mobile where
  // `pagehide`/`beforeunload` don't. Keep `pagehide` as a second chance for
  // desktop tab close.
  document.addEventListener(
    'visibilitychange',
    () => {
      if (document.visibilityState === 'hidden') flush();
    },
    { capture: true },
  );
  window.addEventListener('pagehide', flush, { capture: true });
}

/** Lightweight route-change timing: on every SPA navigation, mark the
 *  moment the URL changed and measure elapsed time to the next settled
 *  paint (double rAF — the standard "next paint has committed" heuristic).
 *  Not a substitute for per-page data-loaded instrumentation, but a cheap,
 *  always-on signal for "did this route change feel instant" without
 *  requiring every page to opt in. */
function trackRouteChanges(): void {
  let lastPathname = window.location.pathname;

  const onRouteSettled = (fromPathname: string, changedAt: number) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        queuedMetrics.push({
          name: 'route-change',
          value: Math.round(performance.now() - changedAt),
          id: `${normalizeRoute(fromPathname)}->${normalizeRoute(window.location.pathname)}`,
        });
      });
    });
  };

  const handleChange = () => {
    const nextPathname = window.location.pathname;
    if (nextPathname === lastPathname) return;
    const changedAt = performance.now();
    const fromPathname = lastPathname;
    lastPathname = nextPathname;
    onRouteSettled(fromPathname, changedAt);
  };

  // react-router's BrowserRouter drives navigation through the native
  // History API — wrap pushState/replaceState so client-side route changes
  // are observable without coupling this module to react-router.
  for (const method of ['pushState', 'replaceState'] as const) {
    const original = history[method];
    history[method] = function (this: History, ...args: Parameters<History[typeof method]>) {
      const result = original.apply(this, args);
      handleChange();
      return result;
    } as History[typeof method];
  }
  window.addEventListener('popstate', handleChange);
}

let initialized = false;

/** Entry point — call once, on idle, from main.tsx. */
export function initRum(): void {
  if (initialized) return;
  initialized = true;

  installFlushListeners();
  trackRouteChanges();

  import('web-vitals')
    .then(({ onLCP, onINP, onCLS, onTTFB }) => {
      const report = (metric: { name: string; value: number; rating: string; id: string }) => {
        queuedMetrics.push({ name: metric.name, value: metric.value, rating: metric.rating, id: metric.id });
      };
      onLCP(report);
      onINP(report);
      onCLS(report);
      onTTFB(report);
    })
    .catch(() => {
      // web-vitals failed to load — RUM is best-effort, never block the app.
    });
}

/** Deferred registration — call from main.tsx after createRoot. Idle-loads
 *  `web-vitals` so it never competes with the app's own boot-critical work. */
export function scheduleRum(): void {
  scheduleIdle(initRum);
}
